import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Component,  Input,  OnChanges,  SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {AttributeFieldValue,  DynamicAttributeDefinition,  DynamicAttributeOption, DynamicAttributeSelection } from './attribute-values-form.model';
import { AttributeDataType } from '@src/app/shared/enums/AttributeDataType.enum';

@Component({
  selector: 'app-attribute-values-form',
  templateUrl: 'attribute-values-form.component.html',
  styleUrl: 'attribute-values-form.component.scss',
  standalone: false
})
export class AttributeValuesFormComponent implements OnChanges {

  @Input() definitions: DynamicAttributeDefinition[] = [];
  @Input() initialSelections: DynamicAttributeSelection[] = [];
  @Input() fieldIdPrefix = 'dynamic-attribute';

  readonly maxMultipleValues = 10;

  attributeValuesForm: FormGroup = this.fb.group({
    attributes: this.fb.array([])
  });

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    const definitionsChanged = changes['definitions'] !== undefined;
    const selectionsChanged = changes['initialSelections'] !== undefined;

    // rebuild when input data changes
    if (!definitionsChanged && !selectionsChanged) {
      return;
    }
    this.buildAttributeControls();
  }

  get attributesArray(): FormArray {
    return this.attributeValuesForm.get('attributes') as FormArray;
  }

  private buildAttributeControls(): void {
    this.attributesArray.clear();

    for (const definition of this.definitions) {
      const initialSelection = this.getInitialSelection(definition.id);
      const attributeGroup = this.createAttributeGroup(definition, initialSelection);

      this.attributesArray.push(attributeGroup);
    }
  }

  private createAttributeGroup(definition: DynamicAttributeDefinition, initialSelection?: DynamicAttributeSelection): FormGroup {
    const selectedValues = initialSelection?.values ?? [];
    const isFixedValues = definition.dataType === AttributeDataType.FIXED_VALUES;
    const isMultipleInput = !isFixedValues && definition.allowMultipleValues;

    let valueControl: FormControl;

    // fixed values use the dropdown ctrl
    if (isFixedValues) {
      const fixedValuesSelection = this.getFixedValuesSelection(selectedValues, definition.allowMultipleValues);
      valueControl = this.fb.control(fixedValuesSelection);
    } else {

      const initialValue = selectedValues[0] ?? null;
      valueControl = this.createValueControl(definition.dataType, initialValue);
    }

    let multipleValueControls: FormControl[] = [];

    if (isMultipleInput) {
      multipleValueControls = this.createInitialMultipleValueControls(definition.dataType, selectedValues);
    }

    return this.fb.group({
      definitionId: [definition.id],
      dataType: [definition.dataType],
      allowMultipleValues: [definition.allowMultipleValues],
      value: valueControl,
      values: this.fb.array(multipleValueControls)
    });
  }

  private getFixedValuesSelection(selectedValues: AttributeFieldValue[],allowMultipleValues: boolean): AttributeFieldValue[] {
    if (allowMultipleValues) {
      return selectedValues;
    }

    return selectedValues.slice(0, 1);
  }

  private createInitialMultipleValueControls(dataType: DynamicAttributeDefinition['dataType'], selectedValues: AttributeFieldValue[]): FormControl[] {
    if (selectedValues.length === 0) {
      const emptyControl = this.createValueControl(dataType, null);
      return [emptyControl];
    }

    return selectedValues.map(value => {
      return this.createValueControl(
        dataType, value
      );
    });
  }

  private createValueControl(dataType: DynamicAttributeDefinition['dataType'], initialValue: AttributeFieldValue | null
  ): FormControl {
    const validators: ValidatorFn[] = [];

    if (dataType === AttributeDataType.INTEGER) {
      validators.push(
        Validators.pattern(/^-?\d+$/)
      );
    }

    if (dataType === AttributeDataType.DECIMAL) {
      validators.push(
        Validators.pattern(/^-?\d+([.,]\d+)?$/)
      );
    }

    return this.fb.control(initialValue, validators);
  }

  private getInitialSelection(definitionId: number): DynamicAttributeSelection | undefined {
    return this.initialSelections.find(selection => {
      return (
        selection.attributeDefinitionId === definitionId
      );
    });
  }

  getMultipleValuesArray(attributeIndex: number): FormArray {
    return this.attributesArray.at(attributeIndex).get('values') as FormArray;
  }

  addMultipleValue(attributeIndex: number): void {
    const valuesArray = this.getMultipleValuesArray(attributeIndex);

    if (valuesArray.length >= this.maxMultipleValues) {
      return;
    }

    const attributeGroup = this.attributesArray.at(attributeIndex);
    const dataType = attributeGroup.get('dataType')?.value;
    const newControl = this.createValueControl(dataType, null);

    valuesArray.push(newControl);
  }

  removeMultipleValue(attributeIndex: number, valueIndex: number): void {
    const valuesArray = this.getMultipleValuesArray(attributeIndex);

    if (valuesArray.length <= 1) {
      return;
    }

    valuesArray.removeAt(valueIndex);
  }

  isFixedValuesAttribute(attribute: AbstractControl): boolean {
    return (
      attribute.get('dataType')?.value === AttributeDataType.FIXED_VALUES
    );
  }

  isTextAttribute(attribute: AbstractControl): boolean {
    return (
      attribute.get('dataType')?.value === AttributeDataType.TEXT
    );
  }

  isIntegerAttribute(attribute: AbstractControl): boolean {
    return (
      attribute.get('dataType')?.value === AttributeDataType.INTEGER
    );
  }

  allowsMultipleValues(attribute: AbstractControl): boolean {
    return (
      attribute.get('allowMultipleValues') ?.value === true
    );
  }

  getAttributeLabel(definition: DynamicAttributeDefinition): string {
    const currentLanguage =this.translate.currentLang || 'en';

    const currentTranslation = definition.translations.find(translation => {
      return (translation.isoCode === currentLanguage);
    });

    if (currentTranslation?.translatedName) {
      return currentTranslation.translatedName;
    }

    const englishTranslation = definition.translations.find(translation => {
      return translation.isoCode === 'en';
    });

    if (englishTranslation?.translatedName) {
      return englishTranslation.translatedName;
    }

    return definition.definitionCode;
  }

  getDropdownOptions(definition: DynamicAttributeDefinition): Array<{ id: number; text: string }> {
    const currentLanguage = this.translate.currentLang || 'en';
    const initialSelection = this.getInitialSelection(definition.id);
    const selectedOptionIds = initialSelection?.values ?? [];
    const dropdownOptions:Array<{ id: number; text: string }> = [];

    for (const option of definition.options) {
      const optionIsSelected = selectedOptionIds.includes(option.id);

      // keep selected inactive options visible
      if (!option.isActive && !optionIsSelected) {
        continue;
      }

      dropdownOptions.push({
        id: option.id,
        text: this.getOptionLabel(
          option,
          currentLanguage
        )
      });
    }

    return dropdownOptions;
  }

  private getOptionLabel(option: DynamicAttributeOption, language: string): string {
    const currentTranslation = option.translations.find(translation => {
      return translation.isoCode === language;
    });

    if (currentTranslation?.translatedValue) {
      return currentTranslation.translatedValue;
    }

    const englishTranslation = option.translations.find(translation => {
      return translation.isoCode === 'en';
    });

    if (englishTranslation?.translatedValue) {
      return englishTranslation.translatedValue;
    }

    return option.valueCode;
  }

  getSelections(): DynamicAttributeSelection[] {
    const selections: DynamicAttributeSelection[] = [];

    for (const control of this.attributesArray.controls) {
      const attributeGroup = control as FormGroup;
      const attributeDefinitionId = attributeGroup.get('definitionId')?.value;

      if (attributeDefinitionId === undefined || attributeDefinitionId === null) {
        continue;
      }

      const values = this.getAttributeValues(attributeGroup);

      // ignore empty atts in payloads
      if (values.length === 0) {
        continue;
      }

      selections.push({
        attributeDefinitionId,
        values
      });
    }

    return selections;
  }

  private getAttributeValues(attributeGroup: FormGroup): AttributeFieldValue[] {
    const isFixedValues = this.isFixedValuesAttribute(attributeGroup);

    if (isFixedValues) {
      const selectedValue = attributeGroup.get('value')?.value;
      return this.normaliseValues(selectedValue);
    }

    const allowMultipleValues = this.allowsMultipleValues(attributeGroup);

    if (allowMultipleValues) {
      const valuesArray = attributeGroup.get('values') as FormArray;
      return this.getFilledValues(valuesArray.value);
    }

    const singleValue = attributeGroup.get('value')?.value;
    return this.getFilledValues([singleValue]);
  }

  private normaliseValues(value: | AttributeFieldValue | AttributeFieldValue[] | null| undefined): AttributeFieldValue[] {
    if (Array.isArray(value)) {
      return this.getFilledValues(value);
    }

    return this.getFilledValues([value]);
  }

  private getFilledValues(values: Array<AttributeFieldValue | null | undefined>): AttributeFieldValue[] {
    return values.filter(
      (value): value is AttributeFieldValue => {
        return (value !== null && value !== undefined && value !== '');
      }
    );
  }

  markAllAsTouched(): void {
    this.attributeValuesForm.markAllAsTouched();
  }
}
